import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import { apiClient } from '../api/client';

// Query keys factory
export const queryKeys = {
  all: ['api'] as const,
  lists: () => [...queryKeys.all, 'list'] as const,
  list: (resource: string, filters?: Record<string, unknown>) =>
    [...queryKeys.lists(), resource, filters] as const,
  details: () => [...queryKeys.all, 'detail'] as const,
  detail: (resource: string, id: string) =>
    [...queryKeys.details(), resource, id] as const,
};

// Generic GET hook
export function useApiQuery<T>(
  endpoint: string,
  queryKey: readonly unknown[],
  params?: Record<string, string | number | boolean>,
  options?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T, Error>({
    queryKey: [...queryKey],
    queryFn: () => apiClient.get<T>(endpoint, params),
    staleTime: 5 * 60 * 10000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    ...options,
  });
}

// Generic POST/PUT/PATCH/DELETE mutation hook
export function useApiMutation<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'>,
) {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TVariables>({
    mutationFn,
    ...options,
    onSettled: async () => {
      // Invalidate and refetch queries after mutation
      await queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
}

// Example: Fetch users list
export function useUsers(filters?: Record<string, string | number | boolean>) {
  return useApiQuery(
    '/users',
    queryKeys.list('users', filters),
    filters, // Pass filters as params
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
}

// Example: Fetch single user
export function useUser(userId: string) {
  return useApiQuery(
    `/users/${userId}`,
    queryKeys.detail('users', userId),
    undefined, // No params
    {
      enabled: !!userId,
    },
  );
}

// Example: Create user mutation
export function useCreateUser() {
  return useApiMutation((data: { name: string; email: string }) =>
    apiClient.post('/users', data),
  );
}

// Example: Update user mutation
export function useUpdateUser() {
  return useApiMutation(
    ({ id, ...data }: { id: string; name?: string; email?: string }) =>
      apiClient.put(`/users/${id}`, data),
  );
}

// Example: Delete user mutation
export function useDeleteUser() {
  return useApiMutation((userId: string) =>
    apiClient.delete(`/users/${userId}`),
  );
}
